import React, { createContext, useContext, useEffect, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import { Destination, Testimonial, Company } from '../services/api';

interface RealtimeContextType {
  destinations: Destination[];
  testimonials: Testimonial[];
  company: Company | null;
  loading: boolean;
  error: string | null;
  refreshDestinations: () => void;
  refreshTestimonials: () => void;
  refreshCompany: () => void;
}

const RealtimeContext = createContext<RealtimeContextType | undefined>(undefined);

export const useRealtime = () => {
  const context = useContext(RealtimeContext);
  if (!context) {
    throw new Error('useRealtime must be used within a RealtimeProvider');
  }
  return context;
};

interface RealtimeProviderProps {
  children: React.ReactNode;
}

export const RealtimeProvider: React.FC<RealtimeProviderProps> = ({ children }) => {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [destinations, setDestinations] = useState<Destination[]>([]);
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [company, setCompany] = useState<Company | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Initialize socket connection
  useEffect(() => {
    const newSocket = io('http://localhost:5000');
    setSocket(newSocket);

    return () => {
      newSocket.close();
    };
  }, []);

  // Socket event listeners for real-time updates
  useEffect(() => {
    if (!socket) return;

    // Destination updates
    socket.on('destination:created', (newDestination: Destination) => {
      setDestinations(prev => [newDestination, ...prev]);
    });

    socket.on('destination:updated', (updatedDestination: Destination) => {
      setDestinations(prev => 
        prev.map(dest => 
          dest._id === updatedDestination._id ? updatedDestination : dest
        )
      );
    });

    socket.on('destination:deleted', ({ id }: { id: string }) => {
      setDestinations(prev => prev.filter(dest => dest._id !== id));
    });

    // Testimonial updates
    socket.on('testimonial:created', (newTestimonial: Testimonial) => {
      setTestimonials(prev => [newTestimonial, ...prev]);
    });

    socket.on('testimonial:updated', (updatedTestimonial: Testimonial) => {
      setTestimonials(prev => 
        prev.map(test => 
          test._id === updatedTestimonial._id ? updatedTestimonial : test
        )
      );
    });

    socket.on('testimonial:deleted', ({ id }: { id: string }) => {
      setTestimonials(prev => prev.filter(test => test._id !== id));
    });

    // Company updates
    socket.on('company:updated', (updatedCompany: Company) => {
      setCompany(updatedCompany);
    });

    return () => {
      socket.off('destination:created');
      socket.off('destination:updated');
      socket.off('destination:deleted');
      socket.off('testimonial:created');
      socket.off('testimonial:updated');
      socket.off('testimonial:deleted');
      socket.off('company:updated');
    };
  }, [socket]);

  // Initial data loading
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Import API functions dynamically to avoid circular dependencies
        const { destinationsApi, testimonialsApi, companyApi } = await import('../services/api');

        const [destinationsData, testimonialsData, companyData] = await Promise.all([
          destinationsApi.getAll(),
          testimonialsApi.getAll(),
          companyApi.get()
        ]);

        setDestinations(destinationsData);
        setTestimonials(testimonialsData);
        setCompany(companyData);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load data');
        console.error('Error loading initial data:', err);
      } finally {
        setLoading(false);
      }
    };

    loadInitialData();
  }, []);

  const refreshDestinations = async () => {
    try {
      const { destinationsApi } = await import('../services/api');
      const data = await destinationsApi.getAll();
      setDestinations(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to refresh destinations');
    }
  };

  const refreshTestimonials = async () => {
    try {
      const { testimonialsApi } = await import('../services/api');
      const data = await testimonialsApi.getAll();
      setTestimonials(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to refresh testimonials');
    }
  };

  const refreshCompany = async () => {
    try {
      const { companyApi } = await import('../services/api');
      const data = await companyApi.get();
      setCompany(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to refresh company info');
    }
  };

  const value: RealtimeContextType = {
    destinations,
    testimonials,
    company,
    loading,
    error,
    refreshDestinations,
    refreshTestimonials,
    refreshCompany,
  };

  return (
    <RealtimeContext.Provider value={value}>
      {children}
    </RealtimeContext.Provider>
  );
};
